import { useState } from 'react';

import { ApiError, api } from '@/api/client';
import {
  Button,
  Caption,
  Card,
  Field,
  Notice,
  Page,
  PageHead,
  TextInput,
} from '@/components/ui';

/**
 * Accepting an invitation.
 *
 * Every account in this system begins as an invitation — including the first
 * administrator of a new agency, created by `python -m app.provision_agency`.
 * The token is shown to the operator once and delivered out of band.
 *
 * This route is deliberately outside the session gate. Someone accepting an
 * invitation has no account yet, so requiring one would be circular.
 */
const MIN_PASSWORD = 12;

export function AcceptInvitationPage() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = password.length >= MIN_PASSWORD && confirm === password;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await api.post('/auth/invitations/accept', { token, password });
      setDone(true);
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <Page>
        <PageHead title="That link is incomplete" />
        <Notice tone="urgent" title="No invitation token">
          This page needs the token from the invitation you were sent. Open the link exactly
          as it was given to you — some mail clients truncate long links, so copying it into
          the address bar is more reliable than clicking.
        </Notice>
      </Page>
    );
  }

  if (done) {
    return (
      <Page>
        <PageHead title="Your account is ready" />
        <Notice tone="positive" title="Invitation accepted">
          Sign in with your email address and the password you just chose.
          {' '}
          If this is an administrator account, the next screen shows a setup key for your
          authenticator app — administrators cannot sign in without one, and the key is
          shown once.
        </Notice>
        <Button onClick={() => { window.location.href = '/'; }}>Go to sign in</Button>
      </Page>
    );
  }

  return (
    <Page>
      <PageHead
        title="Choose a password"
        subtitle="Your invitation is valid. Nobody else has seen this password, including whoever invited you."
      />
      <Card>
        <form onSubmit={submit} className="stack">
          {problem ? <Notice tone="urgent" title="Could not accept">{problem}</Notice> : null}

          <Field
            label="Password"
            hint={`At least ${MIN_PASSWORD} characters. Length matters more than punctuation — a few unrelated words beats a short string of symbols.`}
            error={tooShort ? `${MIN_PASSWORD} characters minimum` : null}
          >
            {(props) => (
              <TextInput
                {...props}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                autoFocus
              />
            )}
          </Field>

          <Field label="Confirm password" error={mismatch ? 'These do not match' : null}>
            {(props) => (
              <TextInput
                {...props}
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
              />
            )}
          </Field>

          <Button type="submit" size="lg" block busy={busy} disabled={!ready}>
            Accept invitation
          </Button>
          <Caption>
            This link can be used once and expires. If it has already been used or has
            lapsed, ask your agency to send another.
          </Caption>
        </form>
      </Card>
    </Page>
  );
}
